import * as React from 'react';
import {cn} from '../lib/utils';
import {Icons} from '../icons';
import {useAutoResizeTextarea} from '../hooks/use-auto-resize-textarea';

export type PromptInputProps = React.FormHTMLAttributes<HTMLFormElement>;

export const PromptInput = React.forwardRef<HTMLFormElement, PromptInputProps>(
  ({className, ...props}, ref) => (
    <form
      ref={ref}
      className={cn(
        'flex w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-background p-4',
        // Focus lives on the inner textarea, so the container mirrors the
        // design system's input focus treatment via focus-within.
        'focus-within:border-ring-muted focus-within:ring-2 focus-within:ring-border',
        className,
      )}
      {...props}
    />
  ),
);
PromptInput.displayName = 'PromptInput';

export type PromptInputTextareaProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const PromptInputTextarea = React.forwardRef<
  HTMLTextAreaElement,
  PromptInputTextareaProps
>(({className, value, ...props}, ref) => {
  const {textAreaRef} = useAutoResizeTextarea(ref, true, value);

  return (
    <textarea
      ref={textAreaRef}
      value={value}
      className={cn(
        'max-h-48 min-h-5 w-full resize-none bg-transparent text-sm leading-5 text-foreground',
        'placeholder:text-muted-foreground outline-hidden focus:outline-hidden',
        className,
      )}
      {...props}
    />
  );
});
PromptInputTextarea.displayName = 'PromptInputTextarea';

export type PromptInputFooterProps = React.HTMLAttributes<HTMLDivElement>;

export const PromptInputFooter = React.forwardRef<
  HTMLDivElement,
  PromptInputFooterProps
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-between', className)}
    {...props}
  />
));
PromptInputFooter.displayName = 'PromptInputFooter';

export type PromptInputToolsProps = React.HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = React.forwardRef<
  HTMLDivElement,
  PromptInputToolsProps
>(({className, children, ...props}, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-2', className)}
    {...props}
  >
    {children ?? (
      <button
        type="button"
        aria-label="Add"
        className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-border-subtle"
      >
        <Icons.add className="size-5" />
      </button>
    )}
  </div>
));
PromptInputTools.displayName = 'PromptInputTools';

export type PromptInputSubmitProps =
  React.ButtonHTMLAttributes<HTMLButtonElement>;

export const PromptInputSubmit = React.forwardRef<
  HTMLButtonElement,
  PromptInputSubmitProps
>(({className, children, ...props}, ref) => (
  <button
    ref={ref}
    type="submit"
    className={cn(
      'relative flex size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/80',
      '[&>svg]:absolute [&>svg]:inset-0 [&>svg]:m-auto',
      'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    {children ?? <Icons.arrowUp className="size-5" />}
  </button>
));
PromptInputSubmit.displayName = 'PromptInputSubmit';
